import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from 'environments/environment';
import { map, Observable } from 'rxjs';

import { AuthMethod } from '../enums/auth-method.enum';
import { WorkerStatusEnum } from '../enums/worker-status.enum';
import { TelegramMessageEntry } from '../models/telegram-message.model';
import { HistoryLoadStateDto } from '../pages/account-info/models/history-load-state.interface';
import { StatusHistory } from '../pages/account-info/models/status-history.interface';
import { AccountManagemntTableItem } from '../pages/account-management/models/account-management-table-item.interface';
import { TelegramAccount } from '../pages/telegram-account/models/telegram-account.interface';

interface BotAccountPayload {
  phone: string;
  accountId?: string;
}

interface BotStartPayload extends BotAccountPayload {
  authMethod?: AuthMethod | null;
}

interface CodePayload extends BotAccountPayload {
  code: string;
}

interface SessionPayload extends BotAccountPayload {
  sessionId: string;
}

export interface QrShareResponse {
  token: string;
  url: string;
  expiresAt: string;
}

@Injectable({ providedIn: 'root' })
export class TelegramAccountService {
  private http = inject(HttpClient);

  getAll(): Observable<AccountManagemntTableItem[]> {
    return this.http.get<AccountManagemntTableItem[]>(`${environment.apiUrl}/tg/accounts`);
  }

  getAccount(id: string): Observable<TelegramAccount> {
    return this.http.get<TelegramAccount>(`${environment.apiUrl}/vault/accounts/${id}`);
  }

  getAccountByPhone(phone: string): Observable<AccountManagemntTableItem | undefined> {
    return this.http
      .get<AccountManagemntTableItem[]>(`${environment.apiUrl}/tg/accounts?search=${encodeURIComponent(phone)}`)
      .pipe(map((accounts) => accounts[0]));
  }

  getAccountByAccountId(accountId: string): Observable<AccountManagemntTableItem | undefined> {
    return this.http
      .get<AccountManagemntTableItem | null>(`${environment.apiUrl}/tg/accounts/${accountId}`)
      .pipe(map((account) => account ?? undefined));
  }

  getAllAvailableToConnect(): Observable<TelegramAccount[]> {
    return this.http.get<TelegramAccount[]>(`${environment.apiUrl}/TelegramAccount/GetAllAvailableAccounts`);
  }

  connect(accountId: string, phone = '', authMethod?: AuthMethod | null): Observable<void> {
    const payload: BotStartPayload = { phone, accountId, authMethod };
    return this.http.post<void>(`${environment.apiUrl}/tg/bots/start`, payload);
  }

  sendCode(accountId: string, code: string, phone = ''): Observable<void> {
    const payload: CodePayload = { phone, accountId, code };
    return this.http.post<void>(`${environment.apiUrl}/tg/bots/send-code`, payload);
  }

  disconnect(accountId: string, phone = ''): Observable<void> {
    const payload: BotAccountPayload = { phone, accountId };
    return this.http.post<void>(`${environment.apiUrl}/tg/bots/stop`, payload);
  }

  qrShare(accountId: string, phone = ''): Observable<QrShareResponse> {
    const payload: BotAccountPayload = { phone, accountId };
    return this.http.post<QrShareResponse>(`${environment.apiUrl}/tg/bots/qr-share`, payload);
  }

  getAllSessions(accountId: string, phone = ''): Observable<{ status: WorkerStatusEnum }> {
    const payload: BotAccountPayload = { phone, accountId };
    return this.http.post<{ status: WorkerStatusEnum }>(`${environment.apiUrl}/tg/bots/request-sessions`, payload);
  }

  getStatusHistories(accountId: string): Observable<StatusHistory[]> {
    return this.http.get<StatusHistory[]>(`${environment.apiUrl}/tg/accounts/${accountId}/history`);
  }

  getHistoryLoadState(accountId: string): Observable<HistoryLoadStateDto | null> {
    return this.http.get<HistoryLoadStateDto | null>(`${environment.apiUrl}/tg/accounts/${accountId}/history-load`);
  }

  downloadHistory(accountId: string, phone = ''): Observable<void> {
    return this.http.post<void>(`${environment.apiUrl}/tg/bots/download-history`, { phone, accountId });
  }

  reloadHistory(accountId: string, phone = ''): Observable<void> {
    return this.http.post<void>(`${environment.apiUrl}/tg/bots/reload-history`, { phone, accountId });
  }

  resumeHistory(accountId: string, phone = ''): Observable<void> {
    return this.http.post<void>(`${environment.apiUrl}/tg/bots/resume-history`, { phone, accountId });
  }

  stopHistory(accountId: string, phone = ''): Observable<void> {
    return this.http.post<void>(`${environment.apiUrl}/tg/bots/stop-history`, { phone, accountId });
  }

  terminateSession(accountId: string, session_id: string, phone = ''): Observable<void> {
    const payload: SessionPayload = { phone, accountId, sessionId: session_id };
    return this.http.post<void>(`${environment.apiUrl}/tg/bots/terminate-session`, payload);
  }

  sendMessage(payload: {
    phone: string;
    phoneDestinations: string[];
    messages: TelegramMessageEntry[];
  }): Observable<void> {
    return this.http.post<void>(`${environment.apiUrl}/tg/message/send`, payload);
  }
}
